import { LogOut, Moon, Sun, UserCheck, Rocket } from "lucide-react";
import { useEffect, useState } from "react";
import { useIdentity } from "@/lib/identity";

function useDarkMode() {
  const [dark, setDark] = useState(() => {
    try {
      const stored = localStorage.getItem("ezipper-theme");
      if (stored === "dark") return true;
      if (stored === "light") return false;
      return window.matchMedia("(prefers-color-scheme: dark)").matches;
    } catch {
      return false;
    }
  });
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    try { localStorage.setItem("ezipper-theme", dark ? "dark" : "light"); } catch (err) { console.warn("Theme: salvare eșuată", err); }
  }, [dark]);
  return [dark, setDark];
}

export default function Header({ view, onGoDirectory, onGoMatrix, onGoInsights, totalCount, onIdentify, onOpenMyProfile, profilePct, onOpenWizard }) {
  const [dark, setDark] = useDarkMode();
  const { me, logout } = useIdentity();

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-40 border-b border-border bg-white/70 dark:bg-black/60 backdrop-blur-xl"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-3">
          <button
            onClick={onGoDirectory}
            data-testid="logo-home-btn"
            className="flex items-center gap-2.5 group flex-shrink-0"
          >
            <img
              src="/zipper-logo.svg"
              alt="Zipper"
              className="h-9 w-auto"
              draggable={false}
            />
            <div className="hidden md:flex flex-col items-start leading-tight">
              <span className="font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">eZipper</span>
              <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">
                Directory
              </span>
            </div>
          </button>

          <nav className="flex items-center gap-1 p-1 rounded-full border border-border bg-zinc-50 dark:bg-zinc-900">
            <button
              data-testid="nav-director-btn"
              onClick={onGoDirectory}
              className={`px-3 sm:px-4 py-1.5 text-sm font-medium rounded-full transition-colors duration-150 ${
                view === "directory"
                  ? "bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-50 shadow-sm"
                  : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"
              }`}
            >
              Director
            </button>
            <button
              data-testid="nav-matrix-btn"
              onClick={onGoMatrix}
              className={`px-3 sm:px-4 py-1.5 text-sm font-medium rounded-full transition-colors duration-150 ${
                view === "matrix"
                  ? "bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-50 shadow-sm"
                  : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"
              }`}
            >
              Matricial
            </button>
            <button
              data-testid="nav-insights-btn"
              onClick={onGoInsights}
              className={`px-3 sm:px-4 py-1.5 text-sm font-medium rounded-full transition-colors duration-150 ${
                view === "insights"
                  ? "bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-50 shadow-sm"
                  : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"
              }`}
            >
              Insights
            </button>
          </nav>

          <div className="flex items-center gap-2 flex-shrink-0">
            {typeof totalCount === "number" && (
              <span
                data-testid="header-employee-count"
                className="hidden lg:inline text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400"
              >
                {totalCount} angajați
              </span>
            )}

            {me ? (
              <div className="flex items-center gap-1 pl-1 pr-1 py-1 rounded-full border border-border bg-zinc-50 dark:bg-zinc-900">
                {typeof profilePct === "number" && profilePct < 100 && (
                  <button
                    data-testid="header-wizard-btn"
                    onClick={onOpenWizard}
                    title={`Profil ${profilePct}% complet — completează-l`}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold text-emerald-700 dark:text-emerald-400 hover:bg-white dark:hover:bg-zinc-800 transition-colors"
                  >
                    <Rocket className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">{profilePct}%</span>
                  </button>
                )}
                <button
                  data-testid="me-profile-btn"
                  onClick={() => onOpenMyProfile?.(me.id)}
                  className="inline-flex items-center gap-2 px-2 py-1 rounded-full hover:bg-white dark:hover:bg-zinc-800 transition-colors"
                >
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 text-white text-[10px] font-bold">
                    {me.initials}
                  </span>
                  <span className="hidden sm:inline text-xs font-medium text-zinc-900 dark:text-zinc-100 max-w-[120px] truncate">
                    {me.name.split(" ")[0]}
                  </span>
                  {(me.is_admin) && (
                    <span data-testid="admin-badge" className="hidden sm:inline text-[9px] uppercase tracking-wider font-bold text-emerald-600 dark:text-emerald-400">
                      Admin
                    </span>
                  )}
                </button>
                <button
                  data-testid="logout-btn"
                  onClick={logout}
                  className="inline-flex items-center justify-center h-7 w-7 rounded-full hover:bg-white dark:hover:bg-zinc-800 text-zinc-500"
                  aria-label="Ieși"
                >
                  <LogOut className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <button
                data-testid="identify-open-btn"
                onClick={onIdentify}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-border text-xs font-medium hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
              >
                <UserCheck className="h-3.5 w-3.5" />
                <span>Sunt eu</span>
              </button>
            )}

            <button
              data-testid="theme-toggle-btn"
              onClick={() => setDark(!dark)}
              aria-label="Toggle theme"
              className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-border hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors duration-150"
            >
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
